// @ts-expect-error external package without included TS types
import Mention from "@tiptap/extension-mention";
// @ts-expect-error external package without included TS types
import tippy, { type Instance as TippyInstance } from "tippy.js";
import { mergeAttributes } from "@tiptap/core";
import { Suggestion } from "@/components/documents/extensions/dynamicMentionSuggestion";
import { dynamicMentionSuggestionPluginKey } from "@/components/documents/extensions/dynamicMentionSuggestionPluginKey";
import { getDynamicMentionSuggestionOptions } from "@/components/documents/extensions/getDynamicMentionSuggestionOptions";
import {
  dynamicValues,
  findDynamicValue,
  getDynamicValues,
  DATE_FORMATS,
  DATE_FORMAT_TOKENS,
  type DateFormatId,
} from "@/components/common/report-builder/dynamicValues";
import { extendMentionInsertRange } from "@/components/documents/extensions/extendMentionInsertRange";
import { dynamicMentionRangeSelectionPlugin } from "@/components/documents/extensions/dynamicMentionRangeSelectionPlugin";

// Global reference to store the current context
let globalReportBuilderContext: any = null;

export const setReportBuilderContextForMentions = (context: any) => {
  globalReportBuilderContext = context;
};

/** Convert snake_case / kebab-case / lowercase to Start Case */
const toStartCase = (str: string = "") =>
  str
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase());

/**
 * DynamicMention – A custom mention extension that offers dynamic placeholders
 * such as Today’s Date and Page Number. Triggered with the `@` character.
 */
const DynamicMention = Mention.extend({
  name: "dynamicMention",

  group: "inline",

  inline: true,

  selectable: true,

  atom: true,

  marks: "_",

  parseHTML() {
    return [
      {
        tag: 'span[data-type="dynamicMention"]',
        priority: 51, // Higher priority than default mark parsing
      },
      {
        // Fallback: also match spans with class="mention" for legacy support
        tag: 'span.mention',
        getAttrs: (dom) => {
          const element = dom as HTMLElement;
          // Only match if it has data-id (not just any span with class mention)
          if (!element.getAttribute("data-id")) {
            return false;
          }
          return null;
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    // For serialization: output a span with all data attributes
    // The text content should match what the user sees in the editor
    let text = `@${node.attrs.label || node.attrs.id || ""}`;
    
    // Add resource slot suffix if applicable (for display purposes)
    if (node.attrs.resourceSlotId) {
      const slot = globalReportBuilderContext?.resourceSlots?.find(
        (s: any) => s.id === node.attrs.resourceSlotId
      );
      if (slot && node.attrs.id?.startsWith("field:")) {
        text = `${text} (${slot.label})`;
      }
    }
    
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        style: "font-size: 1em; line-height: inherit; color: inherit; font-weight: inherit; white-space: pre-wrap; background: transparent;",
      }),
      text,
    ];
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-id"),
        renderHTML: (attributes: any) => {
          if (!attributes.id) {
            return {};
          }
          return {
            "data-id": attributes.id,
          };
        },
      },
      label: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          // Extract label from text content (strip out any resource slot suffix)
          let label = element.textContent || "";
          // Remove @ prefix if present
          label = label.replace(/^@/, "");
          // Remove resource slot suffix like " (Resource 1)" if present
          label = label.replace(/\s*\([^)]+\)\s*$/, "");
          return label;
        },
        renderHTML: () => ({}), // Don't output as HTML attribute - it's in the text content
      },
      category: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-category"),
        renderHTML: (attributes: any) => {
          if (!attributes.category) return {};
          return {
            "data-category": attributes.category,
          };
        },
      },
      path: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-path"),
        renderHTML: (attributes: any) => {
          if (!attributes.path) return {};
          return {
            "data-path": attributes.path,
          };
        },
      },
      resourceSlotId: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-resource-slot-id"),
        renderHTML: (attributes: any) => {
          if (!attributes.resourceSlotId) return {};
          return {
            "data-resource-slot-id": attributes.resourceSlotId,
          };
        },
      },
      dateFormat: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-date-format"),
        renderHTML: (attributes: any) => {
          if (!attributes.dateFormat) return {};
          return {
            "data-date-format": attributes.dateFormat,
          };
        },
      },
    };
  },

  addOptions() {
    return {
      // @ts-expect-error parent() comes from extend() at runtime but is not in Mention typings
      ...this.parent?.(),
      HTMLAttributes: {
        class: "mention",
        "data-type": "dynamicMention",
      },
      renderText({ options, node }: { options: any; node: any }) {
        // Get context data from global reference
        let contextData: any = {};
        
        if (globalReportBuilderContext) {
          const reportData =
            (globalReportBuilderContext.currentReport?.data as Record<
              string,
              unknown
            >) || {};
          contextData = {
            ...reportData,
            resource_slots: globalReportBuilderContext.resourceSlots || [],
            selected_resources:
              (reportData.selected_resources as Record<string, unknown>) || {},
            as_at: (reportData.as_at as string | null) ?? null,
            resourceSlotId: node.attrs.resourceSlotId || null,
            dateFormat: node.attrs.dateFormat || null,
          };
        }
        
        const allValues = getDynamicValues(contextData);
        const dyn = findDynamicValue(node.attrs.id, allValues);
        if (dyn) {
          return dyn.resolve({
            ...contextData,
            resourceSlotId: node.attrs.resourceSlotId,
            dateFormat: node.attrs.dateFormat,
          });
        }
        return `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`;
      },
      suggestion: {
        char: "@",
        startOfLine: false,
        allowedPrefixes: null, // Allow @ to trigger anywhere, not just after spaces
        command: ({
          editor,
          range,
          props,
        }: {
          editor: any;
          range: any;
          props: any;
        }) => {
          // Get current selection marks to preserve styling
          const state = editor.state;
          const insertRange = extendMentionInsertRange(state.doc, range);
          const currentMarks = state.storedMarks || state.selection.$from.marks();
          const marksJSON = currentMarks.map((m: any) => m.toJSON());
          
          editor
            .chain()
            .focus()
            .insertContentAt(insertRange, [
              {
                type: "dynamicMention", // Hardcode name to be safe
                attrs: props,
                marks: marksJSON,
              },
              {
                type: "text",
                text: " ",
                marks: marksJSON,
              },
            ])
            .run();
        },
        // Provide the list of suggestions based on the current query.
        items: ({ query }: { query: string }) => {
          // Get context data from global reference
          let contextData: any = {};
          
          if (globalReportBuilderContext) {
            const reportData =
              (globalReportBuilderContext.currentReport?.data as Record<
                string,
                unknown
              >) || {};
            contextData = {
              ...reportData,
              resource_slots: globalReportBuilderContext.resourceSlots || [],
              selected_resources:
                (reportData.selected_resources as Record<string, unknown>) || {},
              as_at: (reportData.as_at as string | null) ?? null,
            };
          }
          
          const allValues = getDynamicValues(contextData);
          const q = (query || "").toLowerCase();
          const filtered = allValues.filter((item) => {
            const inLabel = item.label.toLowerCase().includes(q);
            const inCategory = (item.category || "").toLowerCase().includes(q);
            const inId = item.id.toLowerCase().includes(q);
            return inLabel || inCategory || inId;
          });
          
          // De-duplicate by id
          const uniqueById = new Map<string, any>();
          filtered.forEach((it) => {
            if (!uniqueById.has(it.id)) uniqueById.set(it.id, it);
          });
          return Array.from(uniqueById.values());
        },
        // Render dropdown using tippy.js (vanilla JS – no React overhead).
        render: () => {
          let popup: TippyInstance | null = null;
          let root: HTMLDivElement;
          let currentStep: "mentions" | "resourceSlots" | "dateFormats" = "mentions";
          let selectedMention: any = null;
          let selectedResourceSlotId: string | null = null;
          let storedProps: any = null;
          let selectedIndex = 0;
          let items: any[] = [];
          // Store the editor, range, and editor ID for multi-step flows where onExit may be called
          let storedEditor: any = null;
          let storedRange: any = null;
          let storedEditorId: string | null = null;

          /**
           * Insert a mention directly using the editor manager or stored editor.
           * This is used when the suggestion system's command may no longer work
           * (e.g., after onExit has been called in a multi-step flow).
           */
          const insertMentionDirectly = (props: any) => {
            const editorManager = globalReportBuilderContext?.editorManager;
            
            // Try to get a fresh editor reference using the stored editor ID
            let editor = null;
            
            if (storedEditorId && editorManager) {
              if (editorManager.getEditor) {
                editor = editorManager.getEditor(storedEditorId);
              }
              if (!editor && editorManager.editors) {
                const managedEditor = editorManager.editors.get(storedEditorId);
                if (managedEditor?.editor) {
                  editor = managedEditor.editor;
                }
              }
            }
            
            // Fall back to active editor from editorManager
            if (!editor && editorManager?.getActiveEditor) {
              editor = editorManager.getActiveEditor();
            }
            
            // Last resort: stored editor (may be stale)
            if (!editor) {
              editor = storedEditor;
            }
            
            if (!editor || !storedRange) {
              return;
            }

            const state = editor.state;
            const insertRange = extendMentionInsertRange(state.doc, storedRange);
            const currentMarks = state.storedMarks || state.selection.$from.marks();
            const marksJSON = currentMarks.map((m: any) => m.toJSON());

            editor
              .chain()
              .focus()
              .insertContentAt(insertRange, [
                {
                  type: "dynamicMention",
                  attrs: props,
                  marks: marksJSON,
                },
                {
                  type: "text",
                  text: " ",
                  marks: marksJSON,
                },
              ])
              .run();
          };

          /**
           * Check if a mention item is a date field that should show the date format picker.
           * Date fields include: frontend date values (id "date", "as_at_date", etc.)
           * and screener columns that end with "_date" or have date type.
           */
          const isDateFieldItem = (item: any): boolean => {
            if (!item) return false;
            const dateIds = [
              "date",
              "as_at_date",
              "as_at_month_year",
              "as_at_iso",
            ];
            const isBuiltInDate = dateIds.includes(item.id);
            let isFieldDate = false;
            if (item.id && item.id.startsWith("field:")) {
              const fieldId = item.id.replace("field:", "");
              if (fieldId.endsWith("_date")) isFieldDate = true;
              if (item.data_type === "date") isFieldDate = true;
            }
            return isBuiltInDate || isFieldDate;
          };

          const createCategoryHeader = (label: string) => {
            const div = document.createElement("div");
            div.className =
              "px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/40";
            div.textContent = toStartCase(label);
            return div;
          };

          const createItemNode = (
            item: { id: string; label: string; category?: string },
            command: (props: any) => void,
            index: number
          ) => {
            const div = document.createElement("div");
            div.className = `flex items-center justify-between px-3 py-2 text-sm cursor-pointer select-none text-slate-700 dark:text-slate-300 ${
              index === selectedIndex
                ? "bg-indigo-50 dark:bg-gray-700 text-indigo-700 dark:text-indigo-400"
                : "hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-700 dark:hover:text-indigo-400"
            }`;
            const labelEl = document.createElement("span");
            labelEl.textContent = item.label;
            div.appendChild(labelEl);
            div.addEventListener("mousedown", (event) => {
              event.preventDefault();
              event.stopPropagation();
              command(item);
            });
            return div;
          };

          const createResourceSlotNode = (
            slot: { id: string; label: string },
            onClick: () => void
          ) => {
            const div = document.createElement("div");
            div.className =
              "flex items-center justify-between px-3 py-2 text-sm cursor-pointer select-none text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-700 dark:hover:text-indigo-400";
            const labelEl = document.createElement("span");
            labelEl.textContent = slot.label;
            div.appendChild(labelEl);
            div.addEventListener("mousedown", (event) => {
              event.preventDefault();
              event.stopPropagation();
              onClick();
            });
            return div;
          };

          const createBackButton = (onClick: () => void, label = "← Back to mentions") => {
            const div = document.createElement("div");
            div.className =
              "px-3 py-2 text-sm cursor-pointer select-none text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 border-b border-slate-200 dark:border-slate-700";
            div.textContent = label;
            div.addEventListener("mousedown", (event) => {
              event.preventDefault();
              event.stopPropagation();
              onClick();
            });
            return div;
          };

          const createDateFormatNode = (
            format: { id: string; label: string; example: string },
            onClick: () => void
          ) => {
            const div = document.createElement("div");
            div.className =
              "flex items-center justify-between px-3 py-2 text-sm cursor-pointer select-none text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-700 dark:hover:text-indigo-400";

            const labelEl = document.createElement("span");
            labelEl.textContent = format.label;
            div.appendChild(labelEl);

            const exampleEl = document.createElement("span");
            exampleEl.className = "text-xs text-slate-500 dark:text-slate-400 ml-2";
            exampleEl.textContent = format.example;
            div.appendChild(exampleEl);

            div.addEventListener("mousedown", (event) => {
              event.preventDefault();
              event.stopPropagation();
              onClick();
            });
            return div;
          };

          /**
           * Creates the help dialog showing available format tokens.
           */
          const createFormatHelpDialog = (onClose: () => void): HTMLElement => {
            const overlay = document.createElement("div");
            overlay.className =
              "absolute inset-0 bg-slate-900/50 dark:bg-slate-900/70 flex items-center justify-center z-10";

            const dialog = document.createElement("div");
            dialog.className =
              "bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full mx-2 overflow-hidden";

            // Dialog header
            const dialogHeader = document.createElement("div");
            dialogHeader.className =
              "flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700";

            const dialogTitle = document.createElement("span");
            dialogTitle.className = "text-sm font-semibold text-slate-700 dark:text-slate-200";
            dialogTitle.textContent = "Format Tokens";
            dialogHeader.appendChild(dialogTitle);

            const closeButton = document.createElement("button");
            closeButton.className =
              "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-lg leading-none";
            closeButton.innerHTML = "&times;";
            closeButton.addEventListener("mousedown", (e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            });
            dialogHeader.appendChild(closeButton);
            dialog.appendChild(dialogHeader);

            // Dialog content - token table
            const content = document.createElement("div");
            content.className = "px-3 py-2 max-h-48 overflow-y-auto";

            const table = document.createElement("table");
            table.className = "w-full text-xs";

            // Table header
            const thead = document.createElement("thead");
            const headerRow = document.createElement("tr");
            headerRow.className = "text-left text-slate-500 dark:text-slate-400";
            ["Token", "Output", "Example"].forEach((text) => {
              const th = document.createElement("th");
              th.className = "pb-1 pr-2 font-medium";
              th.textContent = text;
              headerRow.appendChild(th);
            });
            thead.appendChild(headerRow);
            table.appendChild(thead);

            // Table body
            const tbody = document.createElement("tbody");
            DATE_FORMAT_TOKENS.forEach((token) => {
              const row = document.createElement("tr");
              row.className = "text-slate-600 dark:text-slate-300";

              const tokenCell = document.createElement("td");
              tokenCell.className = "py-0.5 pr-2 font-mono text-indigo-600 dark:text-indigo-400";
              tokenCell.textContent = token.token;
              row.appendChild(tokenCell);

              const descCell = document.createElement("td");
              descCell.className = "py-0.5 pr-2";
              descCell.textContent = token.description;
              row.appendChild(descCell);

              const exampleCell = document.createElement("td");
              exampleCell.className = "py-0.5 text-slate-400 dark:text-slate-500";
              exampleCell.textContent = token.example;
              row.appendChild(exampleCell);

              tbody.appendChild(row);
            });
            table.appendChild(tbody);
            content.appendChild(table);
            dialog.appendChild(content);

            // Example usage section
            const exampleSection = document.createElement("div");
            exampleSection.className =
              "px-3 py-2 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-700";

            const exampleLabel = document.createElement("div");
            exampleLabel.className = "text-xs text-slate-500 dark:text-slate-400 mb-1";
            exampleLabel.textContent = "Example:";
            exampleSection.appendChild(exampleLabel);

            const exampleCode = document.createElement("code");
            exampleCode.className =
              "text-xs font-mono text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded";
            exampleCode.textContent = "MMMM Do, YYYY → March 6th, 2026";
            exampleSection.appendChild(exampleCode);

            dialog.appendChild(exampleSection);
            overlay.appendChild(dialog);

            // Close on overlay click
            overlay.addEventListener("mousedown", (e) => {
              if (e.target === overlay) {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }
            });

            return overlay;
          };

          // Valid date format tokens for validation
          const VALID_DATE_TOKENS = ["YYYY", "YY", "MMMM", "MMM", "MM", "M", "DD", "D", "Do"];
          
          /**
           * Validates that a custom format string contains at least one valid date token.
           * Returns true if valid, false otherwise.
           */
          const isValidDateFormat = (format: string): boolean => {
            if (!format || !format.trim()) return false;
            return VALID_DATE_TOKENS.some(token => format.includes(token));
          };

          /**
           * Creates the custom format input UI.
           * Uses insertMentionDirectly for insertion since suggestion system may have exited.
           */
          const createCustomFormatInput = (
            mention: any,
            resourceSlotId: string | null,
            onBack: () => void
          ): HTMLElement => {
            const container = document.createElement("div");
            container.className = "p-3";

            // Input row
            const inputRow = document.createElement("div");
            inputRow.className = "flex items-center gap-2";

            const input = document.createElement("input");
            input.type = "text";
            input.placeholder = "e.g., MMMM Do, YYYY";
            input.className =
              "flex-1 px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";

            // Validation message element
            const validationMsg = document.createElement("div");
            validationMsg.className = "text-xs text-slate-500 dark:text-slate-400 mt-1 hidden";
            validationMsg.textContent = "Format must contain at least one token (e.g., YYYY, MM, DD)";

            const applyButton = document.createElement("button");
            applyButton.className =
              "px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0";
            applyButton.textContent = "Apply";
            applyButton.disabled = true;

            // Enable/disable apply button based on input validation
            input.addEventListener("input", () => {
              const value = input.value.trim();
              const isValid = isValidDateFormat(value);
              applyButton.disabled = !isValid;
              
              // Show validation message if input has content but no valid tokens
              if (value && !isValid) {
                validationMsg.classList.remove("hidden");
                input.classList.add("border-red-500", "dark:border-red-500");
                input.classList.remove("border-slate-300", "dark:border-slate-600");
              } else {
                validationMsg.classList.add("hidden");
                input.classList.remove("border-red-500", "dark:border-red-500");
                input.classList.add("border-slate-300", "dark:border-slate-600");
              }
            });

            // Helper to apply the custom format and cleanup
            const applyCustomFormatAndClose = () => {
              const customFormat = input.value.trim();
              if (customFormat) {
                // Reset state and cleanup before inserting
                currentStep = "mentions";
                selectedMention = null;
                popup?.destroy();
                popup = null;
                insertMentionDirectly({
                  ...mention,
                  resourceSlotId: resourceSlotId,
                  dateFormat: customFormat,
                });
              }
            };

            // Apply format on button click
            applyButton.addEventListener("mousedown", (e) => {
              e.preventDefault();
              e.stopPropagation();
              applyCustomFormatAndClose();
            });

            // Apply format on Enter key (only if valid)
            input.addEventListener("keydown", (e) => {
              if (e.key === "Enter" && isValidDateFormat(input.value.trim())) {
                e.preventDefault();
                applyCustomFormatAndClose();
              } else if (e.key === "Escape") {
                e.preventDefault();
                onBack();
              }
            });

            inputRow.appendChild(input);
            inputRow.appendChild(applyButton);
            container.appendChild(inputRow);
            container.appendChild(validationMsg);

            // Focus input after render
            setTimeout(() => input.focus(), 0);

            return container;
          };

          /**
           * Shows the date format picker for date field mentions.
           * Uses insertMentionDirectly to insert the mention since the suggestion
           * system may have already called onExit by the time a format is selected.
           * @param mention - The selected mention item
           * @param resourceSlotId - Optional resource slot ID if selected
           */
          const showDateFormatPicker = (
            mention: any,
            resourceSlotId: string | null
          ) => {
            root.innerHTML = "";

            // State for showing custom input vs format list
            let showingCustomInput = false;
            let showingHelpDialog = false;

            const renderContent = () => {
              root.innerHTML = "";

              // Determine back button behavior based on whether we came from resource slot picker
              const hasMultipleSlots = globalReportBuilderContext?.resourceSlots?.length > 1;
              const isFieldMention = mention.id && mention.id.startsWith("field:");

              if (showingCustomInput) {
                // Back button for custom input
                root.appendChild(
                  createBackButton(() => {
                    showingCustomInput = false;
                    renderContent();
                  }, "← Back to formats")
                );

                // Header for custom input with help button
                const customHeader = document.createElement("div");
                customHeader.className =
                  "flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/40";

                const headerText = document.createElement("span");
                headerText.textContent = "Enter Custom Format";
                customHeader.appendChild(headerText);

                const helpButton = document.createElement("button");
                helpButton.className =
                  "w-4 h-4 flex items-center justify-center text-slate-400 hover:text-indigo-500 dark:hover:text-indigo-400 rounded-full border border-slate-300 dark:border-slate-600 hover:border-indigo-500 dark:hover:border-indigo-400 text-xs font-medium transition-colors";
                helpButton.textContent = "?";
                helpButton.title = "View format tokens";
                helpButton.addEventListener("mousedown", (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  showingHelpDialog = true;
                  renderContent();
                });
                customHeader.appendChild(helpButton);
                root.appendChild(customHeader);

                // Custom input UI (without help button - it's now in header)
                root.appendChild(
                  createCustomFormatInput(
                    mention,
                    resourceSlotId,
                    () => {
                      showingCustomInput = false;
                      renderContent();
                    }
                  )
                );
              } else {
                // Main format picker view
                if (isFieldMention && hasMultipleSlots) {
                  root.appendChild(
                    createBackButton(() => {
                      currentStep = "resourceSlots";
                      selectedResourceSlotId = null;
                      showResourceSlotPicker(mention);
                    }, "← Back to resources")
                  );
                } else {
                  root.appendChild(
                    createBackButton(() => {
                      currentStep = "mentions";
                      selectedMention = null;
                      selectedResourceSlotId = null;
                      update(storedProps);
                    })
                  );
                }

                // Header
                const header = document.createElement("div");
                header.className =
                  "px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/40";
                header.textContent = "Select Date Format";
                root.appendChild(header);

                // Add predefined date format options
                DATE_FORMATS.forEach((format) => {
                  root.appendChild(
                    createDateFormatNode(format, () => {
                      // Reset state and cleanup before inserting
                      currentStep = "mentions";
                      selectedMention = null;
                      popup?.destroy();
                      popup = null;
                      insertMentionDirectly({
                        ...mention,
                        resourceSlotId: resourceSlotId,
                        dateFormat: format.id,
                      });
                    })
                  );
                });

                // Add "Custom..." option
                const customOption = document.createElement("div");
                customOption.className =
                  "flex items-center px-3 py-2 text-sm cursor-pointer select-none text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-700 dark:hover:text-indigo-400 border-t border-slate-200 dark:border-slate-700";

                const customLabel = document.createElement("span");
                customLabel.textContent = "Custom...";
                customOption.appendChild(customLabel);

                customOption.addEventListener("mousedown", (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  showingCustomInput = true;
                  renderContent();
                });
                root.appendChild(customOption);
              }

              // Overlay help dialog if showing
              if (showingHelpDialog) {
                root.appendChild(
                  createFormatHelpDialog(() => {
                    showingHelpDialog = false;
                    renderContent();
                  })
                );
              }
            };

            renderContent();
          };

          const showResourceSlotPicker = (mention: any) => {
            root.innerHTML = "";
            
            // Add back button
            root.appendChild(createBackButton(() => {
              currentStep = "mentions";
              selectedMention = null;
              selectedResourceSlotId = null;
              update(storedProps);
            }));

            // Add header
            const header = document.createElement("div");
            header.className = "px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/40";
            header.textContent = `Select Resource for "${toStartCase(mention.label)}"`;
            root.appendChild(header);

            // Get resource slots from context
            const resourceSlots = globalReportBuilderContext?.resourceSlots || [];

            if (resourceSlots.length === 0) {
              // No slots configured - check if date field to show format picker
              if (isDateFieldItem(mention)) {
                currentStep = "dateFormats";
                showDateFormatPicker(mention, null);
              } else {
                // Reset state and cleanup before inserting
                currentStep = "mentions";
                selectedMention = null;
                popup?.destroy();
                popup = null;
                insertMentionDirectly({ ...mention, resourceSlotId: null });
              }
              return;
            }

            // Add resource slot options
            resourceSlots.forEach((slot: any) => {
              root.appendChild(
                createResourceSlotNode(slot, () => {
                  // After selecting resource slot, check if we need date format picker
                  if (isDateFieldItem(mention)) {
                    selectedResourceSlotId = slot.id;
                    currentStep = "dateFormats";
                    showDateFormatPicker(mention, slot.id);
                  } else {
                    // Reset state and cleanup before inserting
                    currentStep = "mentions";
                    selectedMention = null;
                    popup?.destroy();
                    popup = null;
                    insertMentionDirectly({ ...mention, resourceSlotId: slot.id });
                  }
                })
              );
            });
          };

          const update = (props: any) => {
            storedProps = props;
            const { items: newItems, command } = props;

            if (currentStep === "dateFormats" && selectedMention) {
              showDateFormatPicker(selectedMention, selectedResourceSlotId);
              return;
            }
            
            if (currentStep === "resourceSlots" && selectedMention) {
              showResourceSlotPicker(selectedMention);
              return;
            }

            // Flatten items for keyboard navigation index calculation
            // We need to know the true order of rendered items
            items = [];
            const groups: Record<string, any[]> = {};
            newItems.forEach((item: any) => {
              const cat = item.category || "Other";
              if (!groups[cat]) groups[cat] = [];
              if (!groups[cat].some((i) => i.id === item.id))
                groups[cat].push(item);
            });

            // Reconstruct flattened list in render order
            Object.keys(groups)
              .sort()
              .forEach((cat) => {
                groups[cat].forEach((item) => {
                  items.push(item);
                });
              });

            // Ensure selection is within bounds
            if (items.length === 0) {
              selectedIndex = 0;
            } else if (selectedIndex >= items.length) {
              selectedIndex = items.length - 1;
            } else if (selectedIndex < 0) {
              selectedIndex = 0;
            }

            root.innerHTML = "";

            let globalIndex = 0;
            Object.keys(groups)
              .sort()
              .forEach((cat) => {
                root.appendChild(createCategoryHeader(cat));
                groups[cat].forEach((item) => {
                  root.appendChild(createItemNode(item, (selectedItem) => {
                    // Check if this is a field mention that should show resource slot picker
                    const isFieldMention = selectedItem.id && selectedItem.id.startsWith("field:");
                    const hasMultipleSlots = globalReportBuilderContext?.resourceSlots?.length > 1;

                    if (isFieldMention && hasMultipleSlots) {
                      // Show resource slot picker (which will then show date format picker if needed)
                      selectedMention = selectedItem;
                      currentStep = "resourceSlots";
                      showResourceSlotPicker(selectedItem);
                    } else if (isDateFieldItem(selectedItem)) {
                      // Date field without resource slots - show date format picker directly
                      selectedMention = selectedItem;
                      currentStep = "dateFormats";
                      showDateFormatPicker(selectedItem, null);
                    } else {
                      // Insert immediately without additional selection steps
                      command({ ...selectedItem, resourceSlotId: null });
                    }
                  }, globalIndex));
                  globalIndex++;
                });
              });
          };

          return {
            onStart: (props: any) => {
              currentStep = "mentions";
              selectedMention = null;
              selectedResourceSlotId = null;
              selectedIndex = 0;
              items = [];
              // Store editor, range, and editor ID for multi-step flows
              storedEditor = props.editor;
              storedRange = props.range;
              // Try to get the editor ID from the editorManager's activeEditorId
              storedEditorId = globalReportBuilderContext?.editorManager?.activeEditorId || null;
              root = document.createElement("div");
              root.className =
                "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md py-1 shadow-lg overflow-hidden max-h-80 overflow-y-auto w-72";
              root.setAttribute("data-mention-picker", "true");
              update(props);

              // No arrow: global `.tippy-box` uses large border-radius; a Tippy arrow reads as a
              // stray triangle between the `@` caret and the panel. Flat popover reads cleaner here.
              popup = tippy("body", {
                getReferenceClientRect: props.clientRect as any,
                appendTo: () => document.body,
                content: root,
                showOnCreate: true,
                interactive: true,
                trigger: "manual",
                placement: "bottom-start",
                arrow: false,
              })[0];
            },
            onUpdate(props: any) {
              // Update range as user types more query text
              if (props.range) {
                storedRange = props.range;
              }
              update(props);
              if (popup) {
                popup.setProps({
                  getReferenceClientRect: props.clientRect as any,
                });
              }
            },
            onKeyDown(props: any) {
              if (props.event.key === "Escape") {
                popup?.hide();
                return true;
              }
              if (props.event.key === "ArrowUp") {
                selectedIndex = (selectedIndex - 1 + items.length) % items.length;
                update(storedProps);
                // Scroll selected item into view
                const selectedEl = root.children[selectedIndex] as HTMLElement; // This won't work directly because of headers
                // Simplified scroll logic: rerender handles visuals, let's just update index
                return true;
              }
              if (props.event.key === "ArrowDown") {
                selectedIndex = (selectedIndex + 1) % items.length;
                update(storedProps);
                return true;
              }
              if (props.event.key === "Enter") {
                if (items.length > 0) {
                  const item = items[selectedIndex];
                  const { command } = storedProps;
                  
                  // Check if this is a field mention that should show resource slot picker
                  const isFieldMention = item.id && item.id.startsWith("field:");
                  const hasMultipleSlots = globalReportBuilderContext?.resourceSlots?.length > 1;

                  if (isFieldMention && hasMultipleSlots) {
                    selectedMention = item;
                    currentStep = "resourceSlots";
                    showResourceSlotPicker(item);
                  } else if (isDateFieldItem(item)) {
                    // Date field without resource slots - show date format picker
                    selectedMention = item;
                    currentStep = "dateFormats";
                    showDateFormatPicker(item, null);
                  } else {
                    command({ ...item, resourceSlotId: null });
                  }
                  return true;
                }
              }
              return false;
            },
            onExit() {
              // Only destroy popup if we're on the initial mentions step.
              // When in a multi-step flow (dateFormats, resourceSlots), the user is still
              // interacting with the picker and focus may have moved to an input element.
              if (currentStep === "mentions") {
                popup?.destroy();
                popup = null;
                selectedMention = null;
              }
            },
          };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    const suggestion = this.options.suggestion as Record<string, unknown> & {
      char?: string;
    };
    return [
      dynamicMentionRangeSelectionPlugin({ nodeName: this.name }),
      Suggestion(
        getDynamicMentionSuggestionOptions({
          editor: this.editor,
          extensionName: this.name,
          char: suggestion?.char ?? "@",
          overrideSuggestionOptions: {
            ...suggestion,
            pluginKey: dynamicMentionSuggestionPluginKey,
          },
        }),
      ),
    ];
  },
});

export default DynamicMention;
